// src/services/userService.ts
import api from './api';
import {
    ManagedUser,
    BannedUsersResponse,
    BanUserPayload,
    BanUserResponse,
    UnbanUserResponse,
    ChangeRolePayload,
    ChangeRoleResponse,
    UserSessionsResponse,
    RevokeSessionsResponse,
} from '../types/auth';

export interface UserFilters {
    status?: 'Active' | 'Pending' | 'Banned';
    role?: string;
    search?: string;
    skip?: number;
    limit?: number;
}

export interface UpdateUserPayload {
    role?: string;
    status?: 'Active' | 'Pending' | 'Banned';
}

export const fetchUsers = async (filters?: UserFilters): Promise<ManagedUser[]> => {
    const response = await api.get('/users/', { params: filters });
    return response.data;
};

export const getUserById = async (userId: number): Promise<ManagedUser> => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
};

export const updateUser = async (userId: number, payload: UpdateUserPayload): Promise<ManagedUser> => {
    const response = await api.put(`/users/${userId}`, payload);
    return response.data;
};

export const getBannedUsers = async (skip: number = 0, limit: number = 50): Promise<BannedUsersResponse> => {
    const response = await api.get<BannedUsersResponse>('/users/banned', { params: { skip, limit } });
    return response.data;
};

export const banUser = async (userId: number, reason?: string): Promise<BanUserResponse> => {
    const payload: BanUserPayload = reason ? { reason } : {};
    const response = await api.post<BanUserResponse>(`/users/${userId}/ban`, payload);
    return response.data;
};

export const unbanUser = async (userId: number): Promise<UnbanUserResponse> => {
    const response = await api.post<UnbanUserResponse>(`/users/${userId}/unban`);
    return response.data;
};

export const changeUserRole = async (userId: number, newRole: 'Admin' | 'User'): Promise<ChangeRoleResponse> => {
    const payload: ChangeRolePayload = { new_role: newRole };
    const response = await api.post<ChangeRoleResponse>(`/users/${userId}/role`, payload);
    return response.data;
};

export const deleteUser = async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}`);
};

// Session Management (SuperAdmin only)
export const getUserSessions = async (
    userId: number,
    includeExpired: boolean = false
): Promise<UserSessionsResponse> => {
    const response = await api.get<UserSessionsResponse>(`/users/${userId}/sessions`, {
        params: { include_expired: includeExpired },
    });
    return response.data;
};

export const revokeAllUserSessions = async (userId: number): Promise<RevokeSessionsResponse> => {
    const response = await api.delete<RevokeSessionsResponse>(`/users/${userId}/sessions`);
    return response.data;
};

export const revokeUserSession = async (
    userId: number,
    sessionId: number
): Promise<RevokeSessionsResponse> => {
    const response = await api.delete<RevokeSessionsResponse>(
        `/users/${userId}/sessions/${sessionId}`
    );
    return response.data;
};